import {Errors, Requirement} from '../../Shared/components/ErrorPopover';
import {ToMatchOptions} from '../../Shared/utilities/MapDiscriminatedUnion';

export const toRequirement = (password: Password): Requirement[] => [
  {
    isMet: password.meetsCharacterRequirement,
    message: 'Must have at least one lower-case, upper-case, symbol and number.',
  },
  {
    isMet: password.meetsMatchRequirement,
    message: 'Passwords must match.',
  },
  {
    isMet: password.meetsLengthRequirement,
    message: 'Must have at least 8 characters.',
  },
  {
    isMet: true, // We will not know; Informational
    message: 'Password must not have been used in the previous 6 passwords.',
  },
];

export class UnvalidatedPassword {
  public readonly key = 'UnvalidatedPassword';

  public readonly meetsCharacterRequirement = false;
  public readonly meetsLengthRequirement = false;
  public readonly meetsMatchRequirement = false;

  public constructor(public readonly passwordLine1: string, public readonly passwordLine2: string) {}
}

export class InvalidPassword {
  public readonly key = 'InvalidPassword';

  public constructor(
    public readonly passwordLine1: string,
    public readonly passwordLine2: string,
    public readonly meetsCharacterRequirement: boolean,
    public readonly meetsLengthRequirement: boolean,
    public readonly meetsMatchRequirement: boolean
  ) {}
}

export class ValidPassword {
  public static create(passwordLine1: string, passwordLine2: string) {
    const doPasswordsMatch = passwordLine1 === passwordLine2;

    const containsUpperCase = /[A-Z]+/;
    const containsLowerCase = /[a-z]+/;
    const containsSpecialCharacter = /[^A-Za-z0-9]+/;
    const containsNumber = /[0-9]+/;
    const doesPasswordContainCorrectCharacters =
      containsLowerCase.test(passwordLine1) &&
      containsUpperCase.test(passwordLine1) &&
      containsSpecialCharacter.test(passwordLine1) &&
      containsNumber.test(passwordLine1);

    const doesPasswordMeetLengthRequirement = passwordLine1.length >= 8;

    return doesPasswordMeetLengthRequirement && doPasswordsMatch && doesPasswordContainCorrectCharacters
      ? new ValidPassword(passwordLine1, passwordLine2)
      : new InvalidPassword(
          passwordLine1,
          passwordLine2,
          doesPasswordContainCorrectCharacters,
          doesPasswordMeetLengthRequirement,
          doPasswordsMatch
        );
  }
  public readonly meetsCharacterRequirement = true;
  public readonly meetsLengthRequirement = true;
  public readonly meetsMatchRequirement = true;

  public readonly key = 'ValidPassword';

  private constructor(public readonly passwordLine1: string, public readonly passwordLine2: string) {}
}

export type Password = ValidPassword | InvalidPassword | UnvalidatedPassword;

type AgentPasswordOptions<TReturn> = ToMatchOptions<Password, 'key', TReturn>;

export const matchPassword = <TReturn>(password: Password, options: AgentPasswordOptions<TReturn>) => {
  switch (password.key) {
    case 'InvalidPassword':
      return options.InvalidPassword(password);
    case 'UnvalidatedPassword':
      return options.UnvalidatedPassword(password);
    case 'ValidPassword':
      return options.ValidPassword(password);
  }
};

export const getPasswordRequirements = (password: Password) =>
  Errors.requirements({
    value: matchPassword(password, {
      InvalidPassword: invalid => toRequirement(invalid),
      UnvalidatedPassword: unvalidated => toRequirement(unvalidated),
      ValidPassword: valid => toRequirement(valid),
    }),
  });
